import React, { useEffect, useRef } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  blockColumnHeaderHeightState,
  blockDataRowHeightState,
  blockFullContentHeightState,
  blockGridStyleState,
  blockLimitedHoldingsSubjectsWithFees,
  blockTitleBarHeightState,
  isPrintingState,
  isReportState,
} from 'venn-state';
import { useSectorHoldingsTreeGrid } from '../../logic/useSectorHoldingsTreeGrid';
import { useCommonGridProps } from '../../logic/columnUtils';

import ExportableGrid from '../grid/ExportableGrid';
import { GraphicSubjectLabel } from './GraphicSubjectLabel';
import { useIsBlockGridCompact } from '../../logic/useIsBlockGridCompact';
import { useCustomSlice } from '../../../blocks/utils/hooks/use-custom-slice';

export const HoldingsBarChart = ({ selectedRefId }: { selectedRefId: string }) => {
  const isReport = useRecoilValue(isReportState);
  const isPrinting = useRecoilValue(isPrintingState);
  const subject = useRecoilValue(blockLimitedHoldingsSubjectsWithFees(selectedRefId))[0];
  const { chartData, columnDefs } = useSectorHoldingsTreeGrid({ selectedRefId, isBarChart: true });

  const gridProps = useCommonGridProps();
  const isCompact = useIsBlockGridCompact();

  const gridStyle = useRecoilValue(blockGridStyleState(selectedRefId));
  const titleBarHeight = useRecoilValue(blockTitleBarHeightState(selectedRefId));
  const setDataRowHeight = useSetRecoilState(blockDataRowHeightState(selectedRefId));
  const setColumnHeaderHeight = useSetRecoilState(blockColumnHeaderHeightState(selectedRefId));
  const setFullContentHeight = useSetRecoilState(blockFullContentHeightState(selectedRefId));

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isReport) return;
    const container = containerRef.current;
    if (!container) return;

    const gridSizePx = parseFloat(gridStyle.gridSize);
    const rowHeight = Math.floor(gridSizePx * 6 + 1);

    const subjectLabelEl = subject ? container.firstElementChild : null;
    const subjectLabelHeight = subjectLabelEl instanceof HTMLElement ? subjectLabelEl.offsetHeight : 0;
    const columnHeaderBand = subjectLabelHeight + rowHeight;

    const rowCount = chartData?.length ?? 0;
    setDataRowHeight(rowHeight);
    setColumnHeaderHeight(columnHeaderBand);
    setFullContentHeight(titleBarHeight + columnHeaderBand + rowCount * rowHeight);
  }, [
    isReport,
    gridStyle.gridSize,
    titleBarHeight,
    chartData?.length,
    subject,
    setDataRowHeight,
    setColumnHeaderHeight,
    setFullContentHeight,
  ]);

  const displayedChartData = useCustomSlice(chartData ?? [], selectedRefId);

  return (
    <div ref={containerRef}>
      {subject && (isReport || isPrinting) && <GraphicSubjectLabel subject={subject} />}
      {displayedChartData.length > 0 && (
        <ExportableGrid
          {...gridProps}
          exportable
          selectedRefId={selectedRefId}
          rowData={displayedChartData}
          columnDefs={columnDefs}
          isCompact={isCompact}
        />
      )}
    </div>
  );
};
